import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { isEmbedded } from '@/lib/embed'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  expiresAt: string | null
  userId: string | null
  orgId: string | null
  orgName: string | null
  orgPlan: string | null
  userEmail: string | null
  role: string | null
  isAuthenticated: boolean

  setSession: (params: {
    accessToken: string
    refreshToken: string
    expiresAt: string
    userId: string
    orgId: string
    role?: string
  }) => void
  setOrgInfo: (orgName: string, orgPlan?: string) => void
  setUserInfo: (email: string, role: string) => void
  clearSession: () => void
  updateTokens: (accessToken: string, refreshToken: string, expiresAt: string) => void
  // Embed mode: session handed over by the partner parent (access token only, no refresh token).
  applyAccessToken: (accessToken: string, expiresAt: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      userId: null,
      orgId: null,
      orgName: null,
      orgPlan: null,
      userEmail: null,
      role: null,
      isAuthenticated: false,

      setSession: ({ accessToken, refreshToken, expiresAt, userId, orgId, role }) =>
        set({ accessToken, refreshToken, expiresAt, userId, orgId, role: role ?? null, isAuthenticated: true }),

      setOrgInfo: (orgName, orgPlan) => set({ orgName, orgPlan: orgPlan ?? null }),

      setUserInfo: (email, role) => set({ userEmail: email, role }),

      clearSession: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          userId: null,
          orgId: null,
          orgName: null,
          orgPlan: null,
          userEmail: null,
          role: null,
          isAuthenticated: false,
        }),

      updateTokens: (accessToken, refreshToken, expiresAt) =>
        set({ accessToken, refreshToken, expiresAt }),

      applyAccessToken: (accessToken, expiresAt) => {
        const claims = parseJwtClaims(accessToken)
        set((s) => {
          // Different user or org → drop cached profile so Layout refetches it.
          const sameUser = s.userId === claims.uid && s.orgId === claims.oid
          return {
            accessToken,
            refreshToken: null,
            expiresAt,
            userId: claims.uid,
            orgId: claims.oid,
            role: claims.role ?? null,
            isAuthenticated: true,
            ...(sameUser ? {} : { orgName: null, orgPlan: null, userEmail: null }),
          }
        })
      },
    }),
    {
      name: 'qtz-discovery-auth',
      // Embedded sessions are per-tab and must never mix with a top-level login.
      storage: createJSONStorage(() => (isEmbedded() ? sessionStorage : localStorage)),
    }
  )
)

// ── JWT claim extraction ───────────────────────────────────────────────────────

interface JwtClaims {
  uid: string
  oid: string
  role?: string
}

export function parseJwtClaims(token: string): JwtClaims {
  const payload = token.split('.')[1]
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtClaims
}
